import React from 'react';
import { t } from '../../i18n';

const SearchAndFilters = ({
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  sortBy,
  sortOrder,
  showSortDropdown,
  handleSort,
  handleSortOption,
  lang
}) => {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex-1 max-w-md">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              type="text"
              placeholder={
                t("searchPatientsPlaceholder", lang) ||
                "Search patients by name, email, or member ID..."
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 hover:bg-white transition-colors"
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="appearance-none px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 hover:bg-white transition-all duration-200 shadow-sm text-center"
          >
            <option value="all">
              {t("allPatients", lang) || "All Patients"}
            </option>
            <option value="active">
              {t("activePatients", lang) || "Active Patients"}
            </option>
            <option value="inactive">
              {t("inactivePatients", lang) || "Inactive Patients"}
            </option>
          </select>
          <div className="relative">
            <button
              onClick={handleSort}
              className={`px-4 py-3 rounded-xl transition-all duration-200 flex items-center gap-2 ${
                sortBy !== "name" || sortOrder !== "asc"
                  ? "bg-blue-100 text-blue-700 border border-blue-200 hover:bg-blue-200"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z"
                />
              </svg>
              {sortBy !== "name" || sortOrder !== "asc" ? (
                <span className="text-xs font-medium">
                  {sortBy === "name"
                    ? "Name"
                    : sortBy === "email"
                    ? "Email"
                    : sortBy === "memberId"
                    ? "ID"
                    : sortBy === "dob"
                    ? "DOB"
                    : sortBy === "status"
                    ? "Status"
                    : "Date"}
                  {sortOrder === "asc" ? " ↑" : " ↓"}
                </span>
              ) : null}
              <svg
                className={`w-4 h-4 transition-transform duration-200 ${
                  showSortDropdown ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {/* Sort Dropdown */}
            {showSortDropdown && (
              <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200 z-10">
                <div className="py-2">
                  <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
                    {t("sortBy", lang) || "Sort by"}
                  </div>
                  <button
                    onClick={() => handleSortOption("name", "asc")}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                      sortBy === "name" && sortOrder === "asc"
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-700"
                    }`}
                  >
                    <span>{t("nameAZ", lang) || "Name (A-Z)"}</span>
                    {sortBy === "name" && sortOrder === "asc" && (
                      <span className="text-blue-600">✓</span>
                    )}
                  </button>
                  <button
                    onClick={() => handleSortOption("name", "desc")}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                      sortBy === "name" && sortOrder === "desc"
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-700"
                    }`}
                  >
                    <span>{t("nameZA", lang) || "Name (Z-A)"}</span>
                    {sortBy === "name" && sortOrder === "desc" && (
                      <span className="text-blue-600">✓</span>
                    )}
                  </button>
                  <button
                    onClick={() => handleSortOption("email", "asc")}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                      sortBy === "email" && sortOrder === "asc"
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-700"
                    }`}
                  >
                    <span>{t("emailAZ", lang) || "Email (A-Z)"}</span>
                    {sortBy === "email" && sortOrder === "asc" && (
                      <span className="text-blue-600">✓</span>
                    )}
                  </button>
                  <button
                    onClick={() => handleSortOption("email", "desc")}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                      sortBy === "email" && sortOrder === "desc"
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-700"
                    }`}
                  >
                    <span>{t("emailZA", lang) || "Email (Z-A)"}</span>
                    {sortBy === "email" && sortOrder === "desc" && (
                      <span className="text-blue-600">✓</span>
                    )}
                  </button>
                  <button
                    onClick={() => handleSortOption("memberId", "asc")}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                      sortBy === "memberId" && sortOrder === "asc"
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-700"
                    }`}
                  >
                    <span>
                      {t("memberIdAZ", lang) || "Member ID (A-Z)"}
                    </span>
                    {sortBy === "memberId" && sortOrder === "asc" && (
                      <span className="text-blue-600">✓</span>
                    )}
                  </button>
                  <button
                    onClick={() => handleSortOption("memberId", "desc")}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                      sortBy === "memberId" && sortOrder === "desc"
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-700"
                    }`}
                  >
                    <span>
                      {t("memberIdZA", lang) || "Member ID (Z-A)"}
                    </span>
                    {sortBy === "memberId" && sortOrder === "desc" && (
                      <span className="text-blue-600">✓</span>
                    )}
                  </button>
                  <button
                    onClick={() => handleSortOption("status", "asc")}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                      sortBy === "status" && sortOrder === "asc"
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-700"
                    }`}
                  >
                    <span>
                      {t("statusActiveFirst", lang) ||
                        "Status (Active First)"}
                    </span>
                    {sortBy === "status" && sortOrder === "asc" && (
                      <span className="text-blue-600">✓</span>
                    )}
                  </button>
                  <button
                    onClick={() => handleSortOption("status", "desc")}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                      sortBy === "status" && sortOrder === "desc"
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-700"
                    }`}
                  >
                    <span>
                      {t("statusInactiveFirst", lang) ||
                        "Status (Inactive First)"}
                    </span>
                    {sortBy === "status" && sortOrder === "desc" && (
                      <span className="text-blue-600">✓</span>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchAndFilters; 